import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { byReportType } from 'relevant-shared/reportData/reportType';
import { Reports } from '../../../api/relevant';
import Base from '../../../layouts/Base';
import { stores } from '../../../stores';
import useReports from '../../../hooks/useBreakpoint/useReports';
import { useToast } from '../../../hooks/useToast';
import { demoMessage, isDemoUser } from '../../DemoImport/utils';
import { CopyReportDialog } from '../CopyReportDialog';
import Report from '../../Report';

export type UseCopyReportParams = {
	report?: Reports | null;
	type?: string;
	/** When omitted, copy uses `report.settings` only (e.g. overview card). */
	reportComponentRef?: { current: Report | null | undefined };
	isDirty: boolean;
};

/**
 * Opens the "Make a copy" dialog; on confirm creates a new saved report via addReport.
 */
export default function useCopyReport({
	report,
	type,
	reportComponentRef,
	isDirty,
}: UseCopyReportParams) {
	const history = useHistory();
	const { updateReports, reports } = useReports();
	const [, showToast] = useToast();

	return useCallback(() => {
		if (!report?.settings || !type) {
			return;
		}
		const defaultIsPublic = Boolean(report?.isPublic);
		const baseSettings = reportComponentRef?.current
			? reportComponentRef.current.getCurrentProperties()
			: report.settings;
		const defaultTitle = `Copy of ${baseSettings.title}`;

		Base.renderGlobal((close) => (
			<CopyReportDialog
				defaultTitle={defaultTitle}
				defaultIsPublic={defaultIsPublic}
				reportIsDirty={isDirty}
				onClose={close}
				onConfirm={async ({ title, isPublic }) => {
					if (isDemoUser()) {
						demoMessage();
						close();
						return;
					}
					try {
						const settings = { ...baseSettings, title };
						const { id } = await Reports.call('addReport', {
							settings,
							tag: byReportType[type].tag,
							userId: isPublic ? null : stores.identity.user().id,
						});
						updateReports();
						showToast({ message: 'Report copied', status: 'success', timeout: 3 });
						const { reportLocation } = byReportType[type];
						window.open(`${reportLocation}/${id}`, '_blank');
						close();
					} catch (err) {
						console.error(err);
						showToast({ message: 'Failed to copy report', status: 'error' });
					}
				}}
			/>
		));
	}, [report, type, reportComponentRef, history, updateReports, showToast, isDirty]);
}
